// The Disposition & handoff tab's words (PLAN D18). Every entry quotes the line of his glossary (or the vendored
// design-system excerpts) it is taken from; the D0 word suites discover this module and prove each quote verbatim, each
// text inside its quote, nothing forbidden and a pending word marked pending. Words D0 already holds (Validate handoff,
// Confirm HRIS, the closure chain, the milestone stations, Add to talent community, Reason, Not Required) are imported
// from @/mc where they are drawn, never redefined here.

import { defineLabels, type LabelSource } from "@/mc/labels";

const his = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "his" }) as const;
const pending = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "pending" }) as const;

const CAPTURE_DATES = "\"Capture accepted, background check, HRIS hire, start and filled separately.\"";
const TALENT_FIELDS = "\"Capture segment, tags, reason/date added, last contact, notes and prior requisition; do not auto-enroll in campaigns.\"";
const TALENT_ADDS = "\"Recruiter intentionally adds eligible finalists, alternates, silver medalists and timing-related declines/withdrawals to talent community.\"";
const COMP_SEEN = "\"HM limited to recommendation/range, proposed base, bonus, commission, requests/status/final terms. Interviewer none.\"";
const SIGN_ON = "\"Sign-on or approved relocation: organizational leader approval obtained by HM, then HM + HRBP.\"";

/** The tab and its cards. The tab's own name waits for his word (Q-E16). */
export const CLOSURE_LABELS = /* @__PURE__ */ defineLabels({
  tab: pending("Disposition & handoff"),
  dispositionWorklist: his("Disposition worklist"),
  handoffChecklist: his("Handoff checklist"),
  hrisConfirmation: his("HRIS confirmation"),
  milestoneDates: his("Milestone dates"),
  finalTerms: his("Final terms"),
});

/** The five closure dates, each its own event (DC-206; his A-18). */
export const DATE_LABELS = /* @__PURE__ */ defineLabels({
  accepted: his("Accepted"),
  backgroundCheck: his("Background check"),
  hrisHire: his("HRIS hire"),
  start: his("Start"),
  filled: his("Filled"),
  startDate: his("Start date"),
});

/** The three closeout clocks (BRD 7.1). */
export const CLOCK_LABELS = /* @__PURE__ */ defineLabels({
  timeToFill: his("Time to Fill"),
  acceptanceToHris: his("Acceptance to HRIS"),
  acceptanceToStart: his("Acceptance to Start"),
});

/** The HRIS confirmation's facts (Employee ID waits: see UNCONFIRMED_WORDS). */
export const HRIS_LABELS = /* @__PURE__ */ defineLabels({
  source: his("Source"),
  evidence: his("Evidence"),
});

/** The handoff checklist items his configuration names that the glossary carries (Q3, E-49). */
export const ITEM_LABELS = /* @__PURE__ */ defineLabels({
  location: his("Location"),
});

/** The accepted package's final terms the Coordinator reads through field policy (his HR Operations, DC-228). */
export const TERMS_LABELS = /* @__PURE__ */ defineLabels({
  base: his("Base"),
  bonus: his("Bonus"),
  signOn: his("Sign-on"),
  relocation: his("Relocation"),
  lti: his("LTI"),
});

/** The worklist and the designation (DC-207: rows end with the designation; alternates carry their reminder). */
export const WORKLIST_LABELS = /* @__PURE__ */ defineLabels({
  candidate: his("Candidate"),
  stage: his("Stage"),
  undispositioned: his("Undispositioned"),
  alternate: his("Alternate"),
  alternateReminder: his("Alternate closure reminder"),
  eligible: his("Eligible"),
  talentCommunity: his("Talent community"),
  segment: his("Segment"),
  tags: his("Tags"),
  dateAdded: his("Date added"),
  lastContact: his("Last contact"),
  notes: his("Notes"),
  priorRequisition: his("Prior requisition"),
});

/** B3's five reasons a person is added (models/talent.REASONS_ADDED), in his words. */
export const REASON_ADDED_LABELS = /* @__PURE__ */ defineLabels({
  finalist: his("Finalists"),
  alternate: his("Alternates"),
  silver_medalist: his("Silver medalists"),
  timing_decline: his("Timing-related declines"),
  timing_withdrawal: his("Withdrawals"),
});

/**
 * Words the plan shows that are not yet his and not on D0's pending list (PLAN D18, design call 12 and the brief; D5 Q3,
 * E-49). defineLabels cannot hold them either way: as `his` they have no provenance, as `pending` D0's rule refuses a
 * word off its list. They stay here, unregistered, each with the decision that named it, until D0 adds them to G §23
 * (lane debt). b9-words.test.mjs checks each carries a source and nothing forbidden.
 */
export const UNCONFIRMED_WORDS = Object.freeze({
  employeeId: { text: "Employee ID", source: "PLAN D18 · design call 12 + brief", status: "pending" },
  hireDate: { text: "Hire date", source: "PLAN D18 · design call 12 + brief", status: "pending" },
  positionNumber: { text: "Position number", source: "PLAN D18 · design call 12 + brief; D5 Q3 (E-49)", status: "pending" },
  signedOffer: { text: "Signed offer", source: "PLAN D5 · Q3 (E-49)", status: "pending" },
} as const);

export type UnconfirmedKey = keyof typeof UNCONFIRMED_WORDS;
export const unconfirmed = (key: UnconfirmedKey): string => UNCONFIRMED_WORDS[key].text;
